/**
 * Authentic D-shape (half-round) casino blackjack table, matching the reference:
 *
 *   • The DEALER (flat) edge has a centered rectangular NOTCH cut into the felt
 *     toward the players — the dealer's standing cutout. Inside it sit the chip
 *     tray (a row of dark vertical slots) and the card shoe.
 *   • A LAYERED RIM: green felt → inner WOOD rail → outer padded LEATHER bumper
 *     studded with evenly-spaced brass bolts around the whole perimeter.
 *   • The curved player edge is a smooth half-ellipse bulging toward the players.
 *   • A solid pedestal base rests ON THE FLOOR (no float).
 *
 * The silhouette comes from the shared `feltOutline()` in seating.ts so the felt,
 * the rim rings, and the painted markings all agree. Felt markings (betting boxes,
 * arc text) are a CanvasTexture overlay (feltMarkings.ts) — no external asset.
 *
 * Felt/wood maps load via the SUSPENDING `useSuspendingTexture` (guarded.ts) so the
 * materials are created WITH their maps on the first render (deterministic cold/warm);
 * a genuinely-absent file falls back to a palette solid.
 *
 * Draw-call budget: felt(1) + markings(1) + wood rail(1) + leather bumper(1) +
 * brass studs(1 instanced) + gold trim(1) + chip tray(1) + slots(1 instanced) +
 * shoe(1) + slab(1) + pedestal(1) + foot(1) ≈ 12 draw calls.
 */

import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { materials, palette, assetPaths } from '../theme/tokens';
import { TABLE, FLAT_Z, NOTCH, RIM, feltOutline } from './seating';
import { useSuspendingTexture } from '../assets/guarded';
import { buildFeltMarkings, MARKINGS_PLANE } from './feltMarkings';

/**
 * COORDINATE FRAME (the canonical fix). All flat shape geometry below is built in
 * the XY plane and then `rotateX(-PI/2)` is applied. That rotation maps shape
 * (x, y, 0) → world (x, 0, -y) — it NEGATES the second coordinate into world z.
 *
 * `feltOutline()` returns world-frame [x, z] (seating.ts frame: FLAT_Z = -1.45 is
 * the FAR/dealer edge, the curve bulges to +z toward the camera). If we fed `z`
 * straight in as the shape's y, the rotation would flip it and render the table
 * Z-MIRRORED vs the seats / furniture / markings / camera (the bug). So we put
 * `-z` into the shape's y; after the rotation's negation, world z = +z, i.e. the
 * felt lands in the SAME seating frame as everything else. (rotateX(+PI/2) would
 * preserve z but flip the up-normal so the felt faces down — hence we negate the
 * data, not the rotation sign.)
 *
 * The gold trim, brass studs, chip-tray/slot/shoe furniture, seats, croupier and
 * camera are all placed directly in the seating frame (no rotateX), so they need
 * no change — negating z here makes the felt/rim/slab agree with all of them.
 */
function negZ(pts: Array<[number, number]>): Array<[number, number]> {
  // Negating y mirrors the polygon across the x-axis, which REVERSES its winding
  // order; we reverse the point sequence too so the winding (and thus the extrude
  // face normals for the rings/slab) is preserved — only the z-frame flips.
  return pts.map(([x, z]) => [x, -z] as [number, number]).reverse();
}

/** A THREE.Shape from world [x, z] points; negates z so post-rotateX world z is correct. */
function shapeFromPoints(pts: Array<[number, number]>): THREE.Shape {
  const s = new THREE.Shape();
  negZ(pts).forEach(([x, y], i) => (i === 0 ? s.moveTo(x, y) : s.lineTo(x, y)));
  s.closePath();
  return s;
}

/** Filled D felt surface (flat), with the dealer notch cut in. */
function useFeltGeometry(): THREE.ShapeGeometry {
  return useMemo(() => {
    // 144 outline segments → a smooth curved felt edge (the shape is explicit
    // line segments, so density comes from feltOutline's segs, not the tessellation arg).
    const geo = new THREE.ShapeGeometry(shapeFromPoints(feltOutline(0, true, 144)));
    geo.rotateX(-Math.PI / 2);
    return geo;
  }, []);
}

/**
 * A flat ring (extruded) between two plain-D outlines. `innerExpand` <
 * `outerExpand`. Used for the wood rail and the leather bumper. Plain D (no notch)
 * so the rim runs straight across the dealer station.
 */
function ringGeometry(innerExpand: number, outerExpand: number, depth: number, bevel: boolean): THREE.ExtrudeGeometry {
  // 144 outline segments on both rings → smooth concentric wood/leather borders.
  const outer = shapeFromPoints(feltOutline(outerExpand, false, 144));
  const hole = new THREE.Path();
  // Hole must use the SAME negated-z frame as `shapeFromPoints` (see negZ docs).
  hole.setFromPoints(negZ(feltOutline(innerExpand, false, 144)).map(([x, y]) => new THREE.Vector2(x, y)));
  outer.holes.push(hole);
  const geo = new THREE.ExtrudeGeometry(outer, {
    depth,
    bevelEnabled: bevel,
    bevelThickness: bevel ? 0.05 : 0,
    bevelSize: bevel ? 0.05 : 0,
    bevelSegments: bevel ? 2 : 0,
    curveSegments: 64,
  });
  geo.rotateX(-Math.PI / 2);
  return geo;
}

/** Solid table-top slab (gives the table thickness; felt + rim sit on it). */
function useTopSlabGeometry(): THREE.ExtrudeGeometry {
  return useMemo(() => {
    const geo = new THREE.ExtrudeGeometry(shapeFromPoints(feltOutline(RIM.wood + RIM.bumper, false, 144)), {
      depth: 0.14,
      bevelEnabled: false,
      curveSegments: 64,
    });
    geo.rotateX(-Math.PI / 2);
    return geo;
  }, []);
}

/** Width (world meters) of the flat brass inlay strip at the felt edge. */
const INLAY_W = 0.022;

/**
 * Offset a closed CCW polygon inward by a constant world distance. Each vertex is
 * shifted along the average of its two adjacent edges' inward normals (scaled to
 * keep a constant perpendicular offset at the corner), so every edge — the flat
 * dealer edge, the rectangular notch walls, AND the curved player edge — moves
 * inward by the same amount. This nests the inner ring inside the felt outline
 * correctly at the NOTCH (where `feltOutline`'s own `expand` would grow the notch
 * the wrong way, since that param is tuned for the OUTWARD rim rings).
 */
function offsetInward(pts: Array<[number, number]>, dist: number): Array<[number, number]> {
  const n = pts.length;
  const out: Array<[number, number]> = [];
  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n]!;
    const cur = pts[i]!;
    const next = pts[(i + 1) % n]!;
    // Inward normals of the two edges meeting at `cur` (polygon is CCW in [x,z],
    // so the inward normal of edge (a→b) is the left-hand normal (-dz, dx)... but
    // our points are CCW in world XZ where +z is toward camera; the interior is to
    // the LEFT of each directed edge, giving inward normal (-(b.z-a.z), (b.x-a.x))).
    const e1x = cur[0] - prev[0];
    const e1z = cur[1] - prev[1];
    const e2x = next[0] - cur[0];
    const e2z = next[1] - cur[1];
    const n1x = -e1z;
    const n1z = e1x;
    const n2x = -e2z;
    const n2z = e2x;
    const l1 = Math.hypot(n1x, n1z) || 1;
    const l2 = Math.hypot(n2x, n2z) || 1;
    // Average the two unit inward normals; rescale by 1/cos(half-angle) so the
    // perpendicular offset stays `dist` at the corner (miter), clamped for safety.
    let mx = n1x / l1 + n2x / l2;
    let mz = n1z / l1 + n2z / l2;
    const ml = Math.hypot(mx, mz) || 1;
    mx /= ml;
    mz /= ml;
    const cos = mx * (n1x / l1) + mz * (n1z / l1);
    const scale = Math.min(4, 1 / Math.max(0.25, cos));
    out.push([cur[0] + mx * dist * scale, cur[1] + mz * dist * scale]);
  }
  return out;
}

/**
 * Flat brass INLAY tracing the felt's edge (one draw call, matte — no bloom).
 *
 * It is a thin flat ring lying ON the felt, between the felt's EXACT silhouette
 * (`feltOutline(0, true, 144)` — the IDENTICAL outline, notch flag, and density the
 * felt `ShapeGeometry` uses) and a constant-distance INWARD offset of that SAME
 * polyline. Because the outer edge is the felt's own silhouette and the inner edge
 * is a uniform inward offset of it, the inlay hugs the felt's rectangular dealer
 * notch EXACTLY — same straight walls, same sharp corners — with no divergence.
 *
 * (The previous CatmullRom TUBE used a DIFFERENT outline — `expand=0.012`, which
 * shifts the flat edge and shrinks the notch — and the spline smoothed/overshot the
 * sharp notch corners, so the trim and the felt notch did not coincide. A flat ring
 * built from explicit line segments cannot round corners, so the notch reads as one
 * clean shape.)
 */
function useGoldTrim(): THREE.ShapeGeometry {
  return useMemo(() => {
    const outline = feltOutline(0, true, 144);
    // Outer edge = the felt's own silhouette (identical params → exact match).
    const shape = shapeFromPoints(outline);
    // Inner edge = a constant INWARD offset of the SAME polyline (correct at the notch).
    const innerPts = offsetInward(outline, INLAY_W);
    const hole = new THREE.Path();
    hole.setFromPoints(negZ(innerPts).map(([x, y]) => new THREE.Vector2(x, y)));
    shape.holes.push(hole);
    const geo = new THREE.ShapeGeometry(shape);
    geo.rotateX(-Math.PI / 2);
    return geo;
  }, []);
}

/** Evenly-spaced brass stud positions along the bumper centerline (plain D). */
function studPositions(): THREE.Vector3[] {
  // Trace the bumper centerline and resample at a fixed arc-length spacing.
  const mid = (RIM.wood + RIM.wood + RIM.bumper) / 2;
  const ring = feltOutline(mid, false).map(([x, z]) => new THREE.Vector3(x, 0, z));
  // Build cumulative length.
  const pts = ring;
  let total = 0;
  const segLen: number[] = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    const d = a.distanceTo(b);
    segLen.push(d);
    total += d;
  }
  const spacing = 0.28; // ~ stud every 28cm
  const studs: THREE.Vector3[] = [];
  let acc = 0;
  let next = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    const d = segLen[i]!;
    while (next <= acc + d && d > 1e-6) {
      const f = (next - acc) / d;
      studs.push(new THREE.Vector3(a.x + (b.x - a.x) * f, 0, a.z + (b.z - a.z) * f));
      next += spacing;
    }
    acc += d;
  }
  void total;
  return studs;
}

export function Table() {
  const feltGeo = useFeltGeometry();
  const slabGeo = useTopSlabGeometry();
  const trimGeo = useGoldTrim();
  const woodRailGeo = useMemo(() => ringGeometry(0, RIM.wood, 0.1, false), []);
  const bumperGeo = useMemo(
    () => ringGeometry(RIM.wood, RIM.wood + RIM.bumper, 0.16, true),
    [],
  );

  // Wood textures load via the SUSPENDING hook (see guarded.ts): the <Suspense>
  // in Scene.tsx waits until they exist, so the materials below are created WITH their
  // maps present on the first render — deterministic on cold and warm loads, with no
  // post-compile `needsUpdate` hazard.
  //
  // FELT: NO texture map at all. The fabric albedo's woven striations rendered as dark
  // horizontal streak lines, and any map+env reintroduced a glossy hotspot. The felt
  // is now a SOLID matte `palette.felt` (materials.felt: roughness 1, metalness 0,
  // envMapIntensity 0), so the overhead spotlight is a soft, clean diffuse pool on
  // uniform green — no streaks, no shiny reflection. (The normal + roughness maps were
  // already dropped earlier for the same weave/X artifact reason.)
  const woodMap = useSuspendingTexture(assetPaths.woodTexture, { kind: 'color' }) ?? undefined;
  const woodNormal = useSuspendingTexture(assetPaths.woodNormal, { kind: 'linear' }) ?? undefined;

  // Painted felt markings (always available — procedural CanvasTexture).
  const markings = useMemo(() => buildFeltMarkings(), []);
  useEffect(() => () => markings.dispose(), [markings]);

  // Brass studs (instanced) around the bumper.
  const studs = useMemo(() => studPositions(), []);
  const studMesh = useRef<THREE.InstancedMesh>(null);
  const studGeo = useMemo(() => new THREE.CylinderGeometry(0.028, 0.032, 0.05, 12), []);
  useLayoutEffect(() => {
    const mesh = studMesh.current;
    if (!mesh) return;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const s = new THREE.Vector3(1, 1, 1);
    const p = new THREE.Vector3();
    studs.forEach((sp, i) => {
      p.set(sp.x, TABLE.topY + 0.06, sp.z);
      m.compose(p, q, s);
      mesh.setMatrixAt(i, m);
    });
    mesh.count = studs.length;
    mesh.instanceMatrix.needsUpdate = true;
  }, [studs]);

  // The center of the D (for placing the centered slab/pedestal/notch furniture).
  const cz = FLAT_Z + TABLE.radiusZ * 0.5;
  // Notch furniture sits centered in the cutout (x=0), midway in z.
  const notchMidZ = FLAT_Z + NOTCH.depth * 0.5;

  // Chip-tray slot dividers (instanced thin boxes).
  const slotMesh = useRef<THREE.InstancedMesh>(null);
  const slotGeo = useMemo(() => new THREE.BoxGeometry(0.025, 0.05, 0.16), []);
  const slotCount = 9;
  useLayoutEffect(() => {
    const mesh = slotMesh.current;
    if (!mesh) return;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const s = new THREE.Vector3(1, 1, 1);
    const p = new THREE.Vector3();
    const trayW = NOTCH.halfW * 1.5;
    for (let i = 0; i < slotCount; i++) {
      const x = -trayW / 2 + (i / (slotCount - 1)) * trayW;
      p.set(x, TABLE.topY + 0.045, FLAT_Z + 0.12);
      m.compose(p, q, s);
      mesh.setMatrixAt(i, m);
    }
    mesh.count = slotCount;
    mesh.instanceMatrix.needsUpdate = true;
  }, []);

  return (
    <group position={[0, 0, 0]}>
      {/* ---- Base that rests on the floor (no float) — scaled to the bigger table ---- */}
      <mesh position={[0, 0.05, cz]} castShadow receiveShadow>
        <cylinderGeometry args={[1.3, 1.6, 0.1, 32]} />
        <meshStandardMaterial color={palette.wood} roughness={0.5} metalness={0.1} />
      </mesh>
      {/* Column tucks UNDER the slab — its top must stay below the felt (topY) or it
          z-fights the felt over a disc at the center (the dark "X" artifact). */}
      <mesh position={[0, (TABLE.topY - 0.06) / 2, cz]} castShadow receiveShadow>
        <cylinderGeometry args={[0.6, 0.95, TABLE.topY - 0.26, 24]} />
        <meshStandardMaterial color={palette.feltDark} roughness={0.85} metalness={0.05} />
      </mesh>

      {/* ---- Solid top slab (gives the table thickness; felt + rim sit on it) ----
          The slab is an ExtrudeGeometry of depth 0.14, so its TOP face lands at
          position.y + 0.14. At position TABLE.topY - 0.14 that top is exactly
          TABLE.topY — COPLANAR with the felt, which z-fought (horizontal striation
          bands + flicker on camera move, worst at grazing angles). Dropping it an
          extra 0.02 puts the slab top ~2cm BELOW the felt: the felt unambiguously
          wins the depth test (no flicker), and the only place the slab top is still
          visible — the dealer notch — now reads as a correct recessed well. */}
      <mesh geometry={slabGeo} position={[0, TABLE.topY - 0.16, 0]} castShadow receiveShadow>
        <meshPhysicalMaterial {...materials.wood} {...(woodMap ? { map: woodMap } : {})} />
      </mesh>

      {/* ---- Felt playing surface (with the dealer notch) — SOLID matte green, NO
          map (the fabric albedo caused dark horizontal streaks + a glossy hotspot). ---- */}
      <mesh geometry={feltGeo} position={[0, TABLE.topY, 0]} receiveShadow>
        <meshStandardMaterial {...materials.felt} />
      </mesh>

      {/* ---- Painted markings overlay (betting boxes, arc text) ----
          Flat transparent plane sized to the FELT bbox (so the print fills the
          felt, not a central strip) just above the felt; polygonOffset avoids
          z-fighting. Matte (no bloom). Plane center is the felt bbox center. */}
      <mesh position={[0, TABLE.topY + 0.002, MARKINGS_PLANE.centerZ]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[MARKINGS_PLANE.width, MARKINGS_PLANE.depth]} />
        <meshStandardMaterial
          map={markings}
          transparent
          roughness={1}
          metalness={0}
          envMapIntensity={0}
          polygonOffset
          polygonOffsetFactor={-1}
          polygonOffsetUnits={-1}
          depthWrite={false}
        />
      </mesh>

      {/* ---- Inner WOOD rail ---- */}
      <mesh geometry={woodRailGeo} position={[0, TABLE.topY - 0.06, 0]} castShadow receiveShadow>
        <meshPhysicalMaterial
          {...materials.wood}
          {...(woodMap ? { map: woodMap } : {})}
          {...(woodNormal ? { normalMap: woodNormal } : {})}
        />
      </mesh>

      {/* ---- Outer padded LEATHER bumper (raised, beveled = padded look) ---- */}
      <mesh geometry={bumperGeo} position={[0, TABLE.topY - 0.04, 0]} castShadow receiveShadow>
        <meshPhysicalMaterial {...materials.leather} />
      </mesh>

      {/* ---- Brass studs around the bumper (instanced; subtle bloom) ---- */}
      <instancedMesh ref={studMesh} args={[studGeo, undefined, Math.max(1, studs.length)]} castShadow>
        <meshStandardMaterial color={palette.brass} metalness={1} roughness={0.3} emissive={palette.gold3} emissiveIntensity={0.6} />
      </instancedMesh>

      {/* ---- Thin MATTE BRASS inlay at the felt edge (NO emissive/bloom) — a subtle,
          clean line rather than a bright glowing yellow outline. It is a FLAT ring
          lying just above the felt, tracing the felt's EXACT silhouette + notch (same
          feltOutline params), so the edge hugs the real notch with sharp corners. ---- */}
      <mesh geometry={trimGeo} position={[0, TABLE.topY + 0.006, 0]}>
        <meshStandardMaterial color={'#7a5f2a'} metalness={0.6} roughness={0.5} side={THREE.DoubleSide} />
      </mesh>

      {/* ======== Dealer-notch furniture (inside the cutout) ======== */}
      {/* Chip tray housing. */}
      <mesh position={[0, TABLE.topY + 0.02, FLAT_Z + 0.12]} castShadow receiveShadow>
        <boxGeometry args={[NOTCH.halfW * 1.7, 0.06, 0.26]} />
        <meshStandardMaterial color={'#15100c'} roughness={0.7} metalness={0.2} />
      </mesh>
      {/* Chip-tray slot dividers (instanced). */}
      <instancedMesh ref={slotMesh} args={[slotGeo, undefined, slotCount]} castShadow>
        <meshStandardMaterial color={'#0a0806'} roughness={0.8} metalness={0.1} />
      </instancedMesh>
      {/* Card shoe — a small angled wedge to the dealer's right inside the notch. */}
      <mesh position={[NOTCH.halfW * 0.62, TABLE.topY + 0.05, notchMidZ + 0.08]} rotation={[-0.32, 0.2, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.2, 0.16, 0.26]} />
        <meshStandardMaterial color={palette.feltDark} roughness={0.5} metalness={0.3} />
      </mesh>
    </group>
  );
}
